import React from 'react'
import useSWR from 'swr'

import { useLocalStorage } from './useLocalStorage'
import { POLLING_INTERVALS_MS } from '@/config/ui'
import { accountUrls } from '@/config/urls/account'
import { PageMetadata } from '@/pages/_app'
import { get } from '@/utils/fetchers'

import { ApiPageableResponse, PageWorkspace, Workspace } from '@/types/index.d'

export function useWorkspaces(
  pageMetadata?: PageMetadata
): ApiPageableResponse<PageWorkspace> & {
  current: Workspace
  setWorkspaceId: (string) => void
} {
  const [localStorageWorkspaceId, setWorkspaceId] = useLocalStorage<string>(
    'WorkspaceId',
    ''
  )

  const url = pageMetadata?.isPublic ? null : accountUrls.getAccounts

  const { data, error, mutate } = useSWR<PageWorkspace>(url, get(url), {
    refreshInterval: (latestData: PageWorkspace) => {
      const latestWorkspace = getSelectedWorkspace(
        latestData?.content,
        localStorageWorkspaceId,
        pageMetadata?.workspaceOverrideCriteria
      )
      if (
        !pageMetadata?.isPublic &&
        !pageMetadata?.isErrorPage &&
        latestWorkspace?.on_boarding_status !== 'ACTIVATED'
      ) {
        // When a workspace is not ACTIVATED means that there are multiple steps still that could happen
        // there for we keep the workspace data fresh every 20seconds
        return POLLING_INTERVALS_MS['15s']
      }
      return 0
    },
    shouldRetryOnError: !pageMetadata?.isPublic && !pageMetadata?.isErrorPage,
  })
  const selectedWorkspace = getSelectedWorkspace(
    data?.content,
    localStorageWorkspaceId,
    pageMetadata?.workspaceOverrideCriteria
  )

  React.useEffect(() => {
    if (
      selectedWorkspace &&
      selectedWorkspace?.id !== localStorageWorkspaceId
    ) {
      // impersonating users could lead to have the wrong workspaceId
      setWorkspaceId(selectedWorkspace?.id)
    }
  }, [selectedWorkspace, localStorageWorkspaceId, setWorkspaceId])

  return {
    current: selectedWorkspace,
    data,
    error,
    isLoading: url && !data && !error,
    mutate,
    setWorkspaceId,
  }
}

function getSelectedWorkspace(
  workspaces: Workspace[],
  localStorageWorkspaceId: string,
  workspaceOverrideCriteria: (workspaces: Workspace[]) => Workspace
): Workspace {
  let selectedWorkspace =
    workspaceOverrideCriteria && workspaceOverrideCriteria(workspaces)

  if (workspaces?.length) {
    // If the local storage contains an id we search for it
    if (!selectedWorkspace && localStorageWorkspaceId) {
      selectedWorkspace = workspaces.filter(
        ({ id }) => id === localStorageWorkspaceId
      )?.[0]
    }

    // If we can not find the one in local storage we look for the first not expired
    if (!selectedWorkspace) {
      selectedWorkspace = workspaces.filter(
        ({ status }) => status !== 'EXPIRED'
      )?.[0]
    }

    // If nothing was found we get the first one
    if (!selectedWorkspace) {
      selectedWorkspace = workspaces?.[0]
    }
  }

  return selectedWorkspace
}
